// Shared NON-date filtering helpers (text normalisation, Client SPOC lists and the
// Company/SPOC "global filters"). Place at src/utils/filterUtils.js.
//
// ALL date logic — parsing, DD-MM-YYYY formatting, presets, matching — lives in
// components/DateFilter.jsx and is imported from there; nothing date-related is
// implemented here.
import { todayDateFilter, allDateFilter, isDefaultDateFilter, matchesDateFilter } from '../components/DateFilter';

// ---- Text helpers ----------------------------------------------------------
// A name is the same name regardless of letter case or stray whitespace, so every
// comparison and de-duplication in the app goes through these.
export const normText = (v) => (v || '').toString().trim().toLowerCase();
export const sameText = (a, b) => normText(a) === normText(b);

// De-duplicates by trimmed lower-case value and drops blanks. The FIRST spelling
// seen wins, so pass authoritative lists (e.g. the Company Master's spelling)
// before raw row values. Result is sorted.
export function uniqueNormalized(...lists) {
    const seen = new Map();
    lists.forEach((list) => (list || []).forEach((v) => {
        const clean = (v || '').toString().trim();
        if (!clean) return;
        const key = clean.toLowerCase();
        if (!seen.has(key)) seen.set(key, clean);
    }));
    return Array.from(seen.values()).sort((a, b) => a.localeCompare(b));
}

// Client SPOC names out of the /api/companies payload (every company carries its own
// `spocs` list, so that one API is the source). With a company name -> that company's
// SPOCs; without one -> every company's, de-duplicated.
export function spocsFromCompanies(companies, companyName) {
    const names = [];
    (companies || []).forEach((c) => {
        if (companyName && !sameText(c.short_name, companyName)) return;
        (c.spocs || []).forEach((sp) => names.push(sp.spoc_name));
    });
    return uniqueNormalized(names);
}

// ---- Global filters (Company / Client SPOC / Received Date / Action Date) ------
// `dateDefault`: 'today' (Day-wise = today) for list/analytics screens, or 'all'
// for Interviews / Candidate Details, where job-level Received/Action dates must
// not hide interviews and candidates.
const defaultDateFor = (dateDefault) => (dateDefault === 'all' ? allDateFilter() : todayDateFilter());
export const defaultGlobalFilters = (dateDefault = 'today') => ({
    company: '',
    spoc: '',
    receivedDate: defaultDateFor(dateDefault),
    actionDate: defaultDateFor(dateDefault)
});

// Task pages drop Received/Action Date entirely, so this omits those two keys;
// matchesDateFilter treats a missing date filter as "always matches".
export const defaultTaskFilters = () => ({ company: '', spoc: '' });

export const hasActiveGlobalFilters = (f, dateDefault = 'today') =>
    !!(f.company || f.spoc ||
        (f.receivedDate && !isDefaultDateFilter(f.receivedDate, dateDefault)) ||
        (f.actionDate && !isDefaultDateFilter(f.actionDate, dateDefault)));

// Jobs carry `company_name`; tasks and candidates carry `company`. Received Date
// reads the job's date_received and Action Date its action_date by default; a
// page may override either through `accessors`.
const defaultAccessors = {
    company: (r) => r.company ?? r.company_name,
    spoc: (r) => r.client_spoc,
    received: (r) => r.date_received,
    action: (r) => r.action_date
};

export function matchesGlobalFilters(row, f, accessors = {}) {
    const acc = { ...defaultAccessors, ...accessors };
    if (f.company && !sameText(acc.company(row), f.company)) return false;
    if (f.spoc && !sameText(acc.spoc(row), f.spoc)) return false;
    if (!matchesDateFilter(row, f.receivedDate, { get: acc.received })) return false;
    if (!matchesDateFilter(row, f.actionDate, { get: acc.action })) return false;
    return true;
}

export const applyGlobalFilters = (rows, f, accessors) =>
    rows.filter((r) => matchesGlobalFilters(r, f, accessors));